import { create } from 'zustand'
import { shallow } from 'zustand/shallow'
import type {
  FaceData,
  OpticalMeasureFreezeStatus,
} from '../index'

export const LIVE_FREEZE_STATUS: OpticalMeasureFreezeStatus = {
  active: false,
  source: 'live',
}

const isSameFreezeStatus = (
  left: OpticalMeasureFreezeStatus,
  right: OpticalMeasureFreezeStatus
) =>
  left.active === right.active &&
  left.source === right.source &&
  left.mode === right.mode &&
  left.reason === right.reason &&
  left.quality === right.quality &&
  left.processing === right.processing

type FaceDataState = {
  data: FaceData | null
  freezeStatus: OpticalMeasureFreezeStatus
}

type FaceDataActions = {
  setData: (data: FaceData) => void
  setFreezeStatus: (status: OpticalMeasureFreezeStatus) => void
}

export const useFaceDataStore = create<FaceDataState & FaceDataActions>((set) => ({
  data: null,
  freezeStatus: LIVE_FREEZE_STATUS,
  setData: (data) =>
    set((state) => {
      const freezeStatus = data.freeze ?? LIVE_FREEZE_STATUS
      if (isSameFreezeStatus(state.freezeStatus, freezeStatus)) {
        return { data }
      }
      return { data, freezeStatus }
    }),
  setFreezeStatus: (freezeStatus) =>
    set((state) =>
      isSameFreezeStatus(state.freezeStatus, freezeStatus)
        ? state
        : { freezeStatus }
    ),
}))

export const useLiveFaceData = () => useFaceDataStore((s) => s.data)

export const useFreezeStatus = () => useFaceDataStore((s) => s.freezeStatus)

export const usePdFaceData = () =>
  useFaceDataStore(
    (s) => {
      const d = s.data
      if (!d || d.nearPd == null || d.nearLeftPd == null || d.nearRightPd == null) {
        return null
      }
      return {
        pd: d.pd,
        leftPd: d.leftPd,
        rightPd: d.rightPd,
        nearPd: d.nearPd,
        nearLeftPd: d.nearLeftPd,
        nearRightPd: d.nearRightPd,
      }
    },
    shallow
  )

export const useShFaceData = () =>
  useFaceDataStore(
    (s) =>
      s.data
        ? {
            sh: s.data.sh,
            shDebug: s.data.shDebug,
            shAdjustment: s.data.shAdjustment,
            glassesPresence: s.data.glassesPresence,
            finalRerunDebug: s.data.finalRerunDebug,
            shCapture: s.data.shCapture,
            calibrating: s.data.calibrating,
            calibration: s.data.calibration,
            freeze: s.data.freeze,
          }
        : null,
    shallow
  )

export const useHsaFaceData = () =>
  useFaceDataStore(
    (s) =>
      s.data
        ? {
            hsa: s.data.hsa,
            hsaDebug: s.data.hsaDebug,
            hsaCapture: s.data.hsaCapture,
            freeze: s.data.freeze,
          }
        : null,
    shallow
  )

export const usePoseAngles = () =>
  useFaceDataStore(
    (s) => (s.data ? { rotationX: s.data.rotationX, rotationY: s.data.rotationY } : null),
    shallow
  )
